/**
 * Field formatters and display labels for the Security Interests change-history
 * panel. Mirrors the labels used in `SecurityInterestDetailPanel.tsx` so the
 * before/after diff renders identical labels to the row UI itself.
 *
 * Used by: `SecurityInterestDetailPanel.tsx` -> `<ChangeHistory />`.
 */

const fmtCurrency = (v: number): string =>
  `$${v.toLocaleString("en-AU", { minimumFractionDigits: 2 })}`;

const fmtDate = (raw: string): string => {
  try {
    return new Date(raw).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return raw;
  }
};

const COLLATERAL_CLASS_LABELS: Record<string, string> = {
  motor_vehicle: "Motor vehicle",
  allpap_no_exceptions: "ALLPAP (no exceptions)",
  allpap_with_exceptions: "ALLPAP (with exceptions)",
  other_goods: "Other goods",
  account: "Account",
  intangible_property: "Intangible property",
  financial_property: "Financial property",
  chattel_paper: "Chattel paper",
  general_intangible: "General intangible",
  inventory: "Inventory",
  negotiable_instrument: "Negotiable instrument",
};

const PARTY_TYPE_LABELS: Record<string, string> = {
  related_party: "Related party",
  third_party: "Third party",
};

const COLLATERAL_TYPE_LABELS: Record<string, string> = {
  commercial_property: "Commercial property",
  consumer_property: "Consumer property",
};

const FIXED_FLOATING_LABELS: Record<string, string> = {
  fixed: "Fixed",
  floating: "Floating",
};

const UNPERFECTED_REASON_LABELS: Record<string, string> = {
  no_ppsr_registration: "No PPSR registration",
  registration_expired: "Registration expired",
  registration_lapsed: "Registration lapsed",
  other: "Other",
};

const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  merged: "Removed",
};

const OWED_SOURCE_LABELS: Record<string, string> = {
  proof_of_debt: "Proof of debt",
  document: "Document",
  manual: "Practitioner estimate",
};

export const SECURITY_INTEREST_FIELD_FORMATTERS: Record<
  string,
  (value: unknown) => string
> = {
  owedAmount: (v) =>
    typeof v === "number" ? fmtCurrency(v) : String(v ?? "\u2014"),
  owedAmountAsAt: (v) => (typeof v === "string" && v ? fmtDate(v) : "\u2014"),
  owedAmountSource: (v) =>
    OWED_SOURCE_LABELS[String(v)] ?? String(v ?? "\u2014"),
  agreementDate: (v) => (typeof v === "string" && v ? fmtDate(v) : "\u2014"),
  registrationDate: (v) => (typeof v === "string" && v ? fmtDate(v) : "\u2014"),
  collateralClass: (v) =>
    COLLATERAL_CLASS_LABELS[String(v)] ?? String(v ?? "\u2014"),
  collateralType: (v) =>
    COLLATERAL_TYPE_LABELS[String(v)] ?? String(v ?? "\u2014"),
  fixedOrFloating: (v) =>
    FIXED_FLOATING_LABELS[String(v)] ?? String(v ?? "\u2014"),
  partyType: (v) => PARTY_TYPE_LABELS[String(v)] ?? String(v ?? "\u2014"),
  unperfectedReason: (v) =>
    UNPERFECTED_REASON_LABELS[String(v)] ?? String(v ?? "\u2014"),
  status: (v) => STATUS_LABELS[String(v)] ?? String(v ?? "\u2014"),
  isUnperfected: (v) => (v ? "Yes" : "No"),
};

export const SECURITY_INTEREST_FIELD_LABELS: Record<string, string> = {
  owedAmount: "Amount owed",
  owedAmountAsAt: "As at",
  owedAmountSource: "Source",
  agreementDate: "Agreement date",
  registrationDate: "Registration date",
  collateralClass: "Collateral class",
  collateralType: "Collateral type",
  fixedOrFloating: "Fixed or floating",
  partyType: "Party type",
  isUnperfected: "Unperfected",
  unperfectedReason: "Unperfected reason",
  notes: "Notes",
  status: "Status",
};
